import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';

import {
    Box, Button, Flex, Grid, Image, Text
} from 'components/ui';
import ActionButtons from 'components/Content/Happening/ActionButtons';
import HappeningImg from 'components/SharedComponents/HappeningImg';

import localeUtils from 'utils/LanguageLocale';
import locationUtils from 'utils/Location';
import { formatAppointmentDate, formatAppointmentTimeFrame, ensureSephoraPrefix } from 'utils/happening';
import resourceWrapper from 'utils/framework/resourceWrapper';

import { borders, colors, shadows } from 'style/config';

import { SERVICE_BUTTONS } from 'components/Content/Happening/ActionButtons/constants';

import HappeningBindings from 'analytics/bindingMethods/components/Content/Happening/HappeningBindings';

const { getLocaleResourceFile } = localeUtils;

function BookingConfirmationDetails(props) {
    const getText = getLocaleResourceFile('components/Content/Happening/BookingConfirmationDetails/locales', 'BookingConfirmationDetails');

    const {
        artist, confirmationNumber, email, startDateTime, store, serviceInfo = {}
    } = props.bookingInfo;
    const {
        duration, durationInt, displayName: activityName, type, imageUrl
    } = serviceInfo;
    const storeName = store?.displayName;
    const artistName = artist?.displayName;

    const formattedDate = formatAppointmentDate(startDateTime, store.timeZone);
    const formattedTime = formatAppointmentTimeFrame(startDateTime, store.timeZone, durationInt);

    const getCalendarInfo = () => {
        return {
            activityName,
            storeName,
            confirmationNumber,
            startDateTime,
            duration,
            type
        };
    };

    const getFormattedText = resourceWrapper(getText);

    if (typeof window !== 'undefined') {
        HappeningBindings.serviceBookingConfirmationPageLoadAnalytics(activityName, store?.storeId);
    }

    return (
        <Flex
            flexDirection='column'
            gap={[5, null, 6]}
            marginTop={4}
        >
            <Grid gap={[2, 4]}>
                <Flex
                    alignItems='center'
                    gap={2}
                >
                    <Image
                        size={24}
                        src='/img/ufe/filled-checkmark.svg'
                    />
                    <Text
                        is='h1'
                        fontSize={['lg', null, 'xl']}
                        fontWeight='bold'
                    >
                        {getText('youAreBookedTitle')}
                    </Text>
                </Flex>
                <Text
                    is='p'
                    children={getFormattedText(
                        'emailConfirmation',
                        false,
                        <Text
                            fontWeight='bold'
                            children={email}
                        />
                    )}
                />
            </Grid>
            <Flex
                alignItems={'flex-start'}
                gap={3}
            >
                <HappeningImg
                    borderRadius={2}
                    width={['100%', null, 210]}
                    maxWidth={[105, null, '100%']}
                    alt={activityName}
                    src={imageUrl}
                />
                <Flex
                    gap={4}
                    flexDirection='column'
                >
                    <Grid gap={2}>
                        <div>
                            <Text
                                fontWeight='bold'
                                is='p'
                            >
                                {formattedDate}
                            </Text>
                            <Text
                                fontWeight='bold'
                                is='p'
                            >
                                {formattedTime}
                            </Text>
                        </div>
                        <Text is='p'>{activityName}</Text>
                        <div>
                            <Text is='p'>{`${getText('artist')}: ${artistName}`}</Text>
                            <Text is='p'>{ensureSephoraPrefix(storeName)}</Text>
                        </div>
                        <Text
                            fontSize='sm'
                            is='p'
                            color={colors.gray}
                            marginTop={-2}
                        >
                            {`${getText('confirmationNumber')}: ${confirmationNumber}`}
                        </Text>
                    </Grid>
                    <ActionButtons
                        size='sm'
                        width={120}
                        calendarInfo={getCalendarInfo()}
                        reservationDetails={props.bookingInfo}
                        buttons={SERVICE_BUTTONS.UPCOMING}
                        buttonProps={{ minWidth: [109, null, 120], maxWidth: [null, null, 120] }}
                    />
                </Flex>
            </Flex>
            <Box
                paddingX={[4, null, 0]}
                paddingY={[2, null, 0]}
                position={['fixed', null, 'relative']}
                right={0}
                bottom={['calc(var(--bottomNavHeight) - 1px)', null, 0]}
                left={0}
                backgroundColor={[colors.white, null, 'inherit']}
                zIndex={['var(--layer-flyout)', null, 'auto']}
                borderBottom={[`${borders[1]} ${colors.lightGray}`, null, 'none']}
                boxShadow={[shadows.light, null, 'none']}
            >
                <Button
                    width={['100%', null, 203]}
                    variant='secondary'
                    children={getText('viewAllReservations')}
                    onClick={e => {
                        locationUtils.navigateTo(e, '/happening/reservations');
                    }}
                />
            </Box>
        </Flex>
    );
}

export default wrapFunctionalComponent(BookingConfirmationDetails, 'BookingConfirmationDetails');
